import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'

import PanelSwitch from '../../components/PanelSwitch'
import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import {
  type ToolAbout,
  TOOL_CODEC_SELECT_CLASS,
  ToolIoChip,
  WrapLinesPanelSwitch,
} from '../../components/tools/toolChrome'
import {
  BASE64_CODEC_ID,
  base64UrlToUtf8,
  compressionAvailable,
  DEFAULT_ENCODING_CODEC_ID,
  ENCODING_CODECS,
  getCodec,
  isEncodingCodecId,
  MAX_CODEC_INPUT_CHARS,
  URI_COMPONENT_CODEC_ID,
  uriComponentDecode,
  uriComponentEncode,
  utf8ToBase64Url,
  type EncodingCodecId,
  type ToolMode,
} from '../../lib/codecs'

const ENCODING_ABOUT: ToolAbout = {
  description:
    'Run common encodings and escapes on your text in the browser. That includes standard and URL-safe Base64, URI percent encoding in two styles, gzip and deflate through the CompressionStream API, C-style and JSON string escapes, and hex dumps. Options that round-trip offer encode and decode. One-way transforms only encode. Your payload never leaves the tab.',
  libraries: [
    { name: 'MDN: btoa and atob (Base64)', href: 'https://developer.mozilla.org/en-US/docs/Web/API/btoa' },
    { name: 'MDN: TextEncoder and TextDecoder', href: 'https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder' },
    {
      name: 'MDN: encodeURIComponent and decodeURIComponent',
      href: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent',
    },
    {
      name: 'MDN: CompressionStream and DecompressionStream',
      href: 'https://developer.mozilla.org/en-US/docs/Web/API/CompressionStream',
    },
  ],
}

export default function EncodingTool() {
  const [codecId, setCodecId] = useState<EncodingCodecId>(DEFAULT_ENCODING_CODEC_ID)
  const [mode, setMode] = useState<ToolMode>('encode')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [base64UrlSafe, setBase64UrlSafe] = useState(false)
  const [uriFormStyle, setUriFormStyle] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const codec = useMemo(() => getCodec(codecId) ?? ENCODING_CODECS[0], [codecId])
  const encodeOnly = codec.encodeOnly === true

  const statsAlgorithmLabel =
    codecId === BASE64_CODEC_ID && base64UrlSafe ? 'BASE64_URL' : codec.statsLabel

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      if (codec.requiresWebCompression && !compressionAvailable()) {
        setError('CompressionStream / DecompressionStream are not available in this browser.')
        return
      }
      setBusy(true)
      let out: string
      if (codecId === BASE64_CODEC_ID) {
        out =
          mode === 'encode'
            ? base64UrlSafe
              ? utf8ToBase64Url(input)
              : await Promise.resolve(codec.encode(input))
            : base64UrlSafe
              ? base64UrlToUtf8(input)
              : await Promise.resolve(codec.decode(input))
      } else if (codecId === URI_COMPONENT_CODEC_ID) {
        out =
          mode === 'encode'
            ? uriComponentEncode(input, uriFormStyle)
            : uriComponentDecode(input, uriFormStyle)
      } else {
        out =
          mode === 'encode'
            ? await Promise.resolve(codec.encode(input))
            : await Promise.resolve(codec.decode(input))
      }
      setOutput(out)
    } catch (e) {
      setError(e instanceof Error ? e.message : mode === 'encode' ? 'Encode failed' : 'Decode failed')
    } finally {
      setBusy(false)
    }
  }, [input, mode, codec, codecId, base64UrlSafe, uriFormStyle])

  const decodePlaceholder = useMemo(() => {
    if (codecId === BASE64_CODEC_ID && base64UrlSafe) return 'Paste base64 or base64url…'
    if (codecId === URI_COMPONENT_CODEC_ID && uriFormStyle) return 'hello+world…'
    return codec.placeholderDecode
  }, [codecId, base64UrlSafe, uriFormStyle, codec.placeholderDecode])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const swap = useCallback(() => {
    if (encodeOnly) return
    setInput(output.slice(0, MAX_CODEC_INPUT_CHARS))
    setOutput('')
    setMode((m) => (m === 'encode' ? 'decode' : 'encode'))
    setError(null)
  }, [output, encodeOnly])

  const onCodecChange = useCallback((id: string) => {
    if (!isEncodingCodecId(id)) return
    setCodecId(id)
    const next = getCodec(id)
    if (next?.encodeOnly) setMode('encode')
    setBase64UrlSafe(false)
    setUriFormStyle(false)
    setOutput('')
    setError(null)
  }, [])

  const modeStatLabel = useMemo(() => {
    if (encodeOnly) return 'NORMALIZE'
    return mode === 'encode' ? 'ENCODE' : 'DECODE'
  }, [encodeOnly, mode])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      wrapLines={lineWrap}
      placeholder={mode === 'encode' ? codec.placeholderEncode : decodePlaceholder}
      aria-label="Encoding input"
    />
  )

  const toolSettings = (
    <>
      {codecId === BASE64_CODEC_ID ? (
        <PanelSwitch
          label="URL-safe (base64url)"
          description="Dash and underscore instead of plus and slash; padding omitted when encoding."
          checked={base64UrlSafe}
          onCheckedChange={(next) => {
            setBase64UrlSafe(next)
            setOutput('')
            setError(null)
          }}
        />
      ) : null}
      {codecId === URI_COMPONENT_CODEC_ID ? (
        <PanelSwitch
          label="Form-style spaces"
          description="Plus for spaces instead of percent-encoding; common when posting HTML forms."
          checked={uriFormStyle}
          onCheckedChange={(next) => {
            setUriFormStyle(next)
            setOutput('')
            setError(null)
          }}
        />
      ) : null}
      <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />
    </>
  )

  return (
    <>
      <Head title="Encodings & escapes" />
      <ToolWorkspace
        heading="Encodings & escapes"
        inputHeaderStart={<ToolIoChip>INPUT (RAW)</ToolIoChip>}
        algorithmSelector={
          <select
            value={codecId}
            onChange={(e) => onCodecChange(e.target.value)}
            aria-label="Algorithm"
            className={TOOL_CODEC_SELECT_CLASS}
          >
            {ENCODING_CODECS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
                {c.requiresWebCompression && !compressionAvailable() ? ' (unsupported here)' : ''}
              </option>
            ))}
          </select>
        }
        toolSettings={toolSettings}
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly={encodeOnly}
        swapDisabled={encodeOnly}
        swapTitle={encodeOnly ? 'Swap needs a reversible codec' : 'Swap I/O'}
        onSwap={swap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm={statsAlgorithmLabel}
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel={modeStatLabel}
        outputLineNumbers
        lineWrap={lineWrap}
        about={ENCODING_ABOUT}
      />
    </>
  )
}
